import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ClaimServices, ClaimStatuses } from "../../../types/travelClaims";
import { AssignTravelManagerForm } from "../../chat/statusChanging/AssignTravelManagerForm";
import { QueryClient, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAlert } from "../../../context/alert";
import { finishTravelClaim } from "../../../services/travelClaims";

interface ConfirmProps {
    onClick: (pk: string) => void
    claimId: string
    isLoading: boolean
    isDisabled: boolean
    unreadCount?: number
    claimType: string | undefined
    claimStatus?: ClaimStatuses
}

export const ConfirmButton = ({ claimId, onClick, isLoading, isDisabled }: ConfirmProps) => {
    return (
        <div
            className="
            w-full inline-flex
            rounded-b-lg flex-col
            mt-auto items-start
            gap-2.5 p-5 bg-white
            shadow-shadow-navigation
            "
        >
            <button
                disabled={isLoading || isDisabled}
                onClick={() => onClick(claimId)}
                className="
                w-full inline-flex items-center justify-center
                gap-2.5 px-5 py-4
                relative
                disabled:bg-gray-300
                flex-[0_0_auto] bg-[#fcd700] rounded-lg"
            >
                <div
                    className="
                    relative w-full
                    font-manager-acrom-button4-16-medium-acrom font-[number:var(--manager-acrom-button4-16-medium-acrom-font-weight)] text-black text-[length:var(--manager-acrom-button4-16-medium-acrom-font-size)] text-center tracking-[var(--manager-acrom-button4-16-medium-acrom-letter-spacing)] leading-[var(--manager-acrom-button4-16-medium-acrom-line-height)] [font-style:var(--manager-acrom-button4-16-medium-acrom-font-style)]
                    "
                >
                    Сохранить изменения
                </div>
            </button>
        </div>
    );
}

interface CloseProps {
    claimId: string
    claimStatus?: ClaimStatuses
    claimService?: ClaimServices
}

export const ButtonsGroup = ({ claimId, claimStatus, claimService }: CloseProps) => {
    const queryClient = useQueryClient();
    const [status, setStatus] = useState(claimStatus)
    const navigate = useNavigate();

    const [buttonClass, setButtonClass] = useState('');
    const [cursorClass, setCursorClass] = useState('');

    const { setInfo } = useAlert();

    const { mutate: update, isPending } = useMutation({
        mutationFn: ({ pk }: { pk: string }) => finishTravelClaim(pk),
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ['travelClaim', claimId.toString()] })
            queryClient.invalidateQueries({ queryKey: ['travelClaims'] })
            setInfo('Заявка закрыта')
        }
    });

    //
    const handleClose = (pk: string) => {
        update({ pk })
    }

    // Если заявка не закрыта, то показывается кнопка закрыть, иначе 2 кнопки по 49% ширины
    useEffect(() => {
        if (!['FINISHED', 'ISSUED'].includes(status || '')) {
            setButtonClass(' w-[32%] ')
        } else {
            setButtonClass(' w-[49%] ')
        }
    }, [status]);

    // Отображение курсора на кнопке
    useEffect(() => {
        if (isPending) {
            setCursorClass(' cursor-progress text-melon-grey ')
        } else {
            setCursorClass(' text-error ')
        }
    }, [isPending]);

    useEffect(() => {
        setStatus(claimStatus)
    }, [claimStatus]);


    return (
        <div className="inline-flex w-full flex-col mt-auto items-start gap-2.5 p-5 bg-white shadow-shadow-navigation">
            <div className="inline-flex w-full flex-col items-center gap-2.5 relative flex-[0_0_auto]">

                <div
                    className="inline-flex w-full flex-row items-start justify-between gap-2.5 relative flex-[0_0_auto]">
                    {/*КНОПКА НАЗНАЧЕНИЯ МЕНЕДЖЕРА*/}
                    <div className={buttonClass}>

                        <AssignTravelManagerForm
                            claim_pk={claimId}
                            disabled={claimService === 'TRIVIO' && ['EXCHANGED', 'CANCELLED', 'ISSUED'].includes(claimStatus || '')}
                        />
                    </div>
                    {/*КНОПКА РЕДАКТИРОВАНИЯ*/}
                    <button
                        disabled={claimService === 'TRIVIO'}
                        title={claimService === 'TRIVIO' ? 'Заявки из Trivio нельзя редактировать напрямую' : undefined}
                        className={"flex flex-col h-full items-center justify-center gap-2.5 px-1 py-3 relative bg-[#f2f2f2] rounded-lg overflow-hidden disabled:opacity-50 " + buttonClass}
                        onClick={() => navigate(`edit`, { 'replace': false })}
                    >
                        <div
                            className="
                            relative w-full
                            text-black
                            break-words
                            text-center
                            font-manager-acrom-ph1-16-medium-acrom
                            font-[number:var(--manager-acrom-ph1-16-medium-acrom-font-weight)]
                            text-[0.9em]
                            tracking-[var(--manager-acrom-ph1-16-medium-acrom-letter-spacing)]
                            leading-[var(--manager-acrom-ph1-16-medium-acrom-line-height)]
                            [font-style:var(--manager-acrom-ph1-16-medium-acrom-font-style)]
                            "
                        >
                            Редактировать
                        </div>
                    </button>
                    {/*КНОПКА ЗАКРЫТИЯ*/}
                    {!['FINISHED', 'ISSUED'].includes(status || '') &&
                        <button
                            disabled={isPending}
                            onClick={() => handleClose(claimId)}
                            className={"flex flex-col h-full w-[32%] items-center justify-center gap-2.5 px-1 py-3 relative bg-[#f2f2f2] rounded-lg overflow-hidden " + cursorClass}>
                            <div
                                className="
                                flex flex-row justify-center items-center
                                relative
                                w-full
                                text-[0.9em]
                                font-manager-acrom-ph1-16-medium-acrom
                                font-[number:var(--manager-acrom-ph1-16-medium-acrom-font-weight)]
                                tracking-[var(--manager-acrom-ph1-16-medium-acrom-letter-spacing)]
                                leading-[var(--manager-acrom-ph1-16-medium-acrom-line-height)]
                                [font-style:var(--manager-acrom-ph1-16-medium-acrom-font-style)]
                                "
                            >
                                Закрыть заявку
                            </div>
                        </button>}
                </div>
            </div>
        </div>
    );
}